import { Button } from '@thanx/uikit/button'
import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import {
  useGetCannedResponsesQuery,
  useRegenerateCannedResponsesMutation,
} from 'api/recoveryAi'
import Spinner from 'components/Spinner'
import { buildTranslate } from 'locales'
import React, { useCallback } from 'react'
import { RECOVERY_AI_CONTENT_MAX_WIDTH } from 'scenes/RecoveryAI/components/layout'
import { successNotificationOverrides } from 'scenes/RecoveryAI/components/successNotificationOverrides'
import { useStyletron } from 'styletron-react'
import { useAbility } from 'utilities/ability'
import { formatDateTime } from 'utilities/dateFormatters'

const t = buildTranslate('recovery_ai.settings.canned_responses')

// The shared formatters return '' for a missing/unparseable timestamp, so render
// the line only when there is something real to show — a bare "Updated " is worse
// than omitting it.
const updatedLabel = (generatedAt: string | null): string | null => {
  const formatted = formatDateTime(generatedAt)
  return formatted ? t('generated_at', { timestamp: formatted }) : null
}

// Settings → Canned responses (AI-249). Per-merchant 5-message set (greeting
// first), generated in the merchant's brand voice (nexus store, #837/#840).
// Fail-soft: an empty/errored fetch degrades to a notice, never a red alert.
//
// AI-932: the pane now shows WHEN each message was generated and offers a manual
// rebuild. Previously it rendered only a binary generated/default badge and threw
// away the generated_at it already fetched, so stale and freshly-rebuilt copy were
// pixel-identical — and because regeneration only ever ran as a side effect of a
// brand-voice save landing on an async worker, a merchant had no way to tell "my
// brand voice was applied" from "nothing ever ran".
const CannedResponsesPane = () => {
  const [css] = useStyletron()
  // AI-932: admin/manage-only write, mirroring every other write control in this
  // scene (EscalationRulesPane, RestrictedCustomers). The BE gates the same way
  // (rbac_authorize! 'manage' + require_admin! fallback), so a view-only user who
  // reached this button would only ever get a 403.
  const ability = useAbility()
  const canManage = ability.can('manage', 'MerchantSettings')

  // isError is deliberately NOT gated on: a 404/empty store must degrade to the
  // plain notice below, never a broken pane (mirrors the makeGoodLadder consumer).
  // Regeneration is async on the BE brand-voice save, so tag invalidation would
  // fire before the new copy exists and just re-cache the stale set. Refetch on
  // every tab-open instead. (The manual rebuild below DOES invalidate — by then
  // the new copy is already committed.)
  const {
    data = [],
    isLoading,
    isFetching,
  } = useGetCannedResponsesQuery(undefined, { refetchOnMountOrArgChange: true })
  const [
    regenerate,
    {
      isLoading: isRegenerating,
      isError: didRegenerateFail,
      isSuccess: didRegenerateSucceed,
      reset,
    },
  ] = useRegenerateCannedResponsesMutation()

  // .unwrap().catch keeps the rejection handled (mirrors useMakeGoodCuration);
  // the inline notice is driven off isError, not a thrown value. reset() clears
  // any stale success/error banner from a prior attempt before firing the next.
  const onRegenerate = useCallback(() => {
    reset()
    regenerate()
      .unwrap()
      .catch(() => {})
  }, [regenerate, reset])

  if (isLoading) {
    // The shared Spinner renders no role/aria text, so wrap it in a named live
    // status region (WCAG 4.1.2 Name, Role, Value) — mirrors the sibling panes.
    return (
      <div role="status" aria-label={t('loading')}>
        <Spinner isLoading size="4x" className="text-center margin-top-huge" />
      </div>
    )
  }

  // L4 keeps this flag-gated pane out of the SettingsPaneChrome extraction; only
  // the shared width constant is adopted (C3/L24).
  return (
    <div className={`p-l ${css({ maxWidth: RECOVERY_AI_CONTENT_MAX_WIDTH })}`}>
      <div className="d-flex flex-column mb-xl">
        <Text.Header3 bold>{t('title')}</Text.Header3>
        <Text.BodyText3 color="grey70" className="mt-xs">
          {t('description')}
        </Text.BodyText3>
      </div>

      {didRegenerateFail && (
        <div className="mb-m">
          {/* baseui's Notification renders Toast under the hood, which defaults
              Body to role="alert" (assertive) — already announced to screen
              readers with no override needed. closeable/onClose was the actual
              gap: without it the banner was stuck until unmount or another
              attempt. */}
          <Notification kind="negative" size="full" closeable onClose={reset}>
            {t('regenerate_error')}
          </Notification>
        </div>
      )}

      {didRegenerateSucceed && !didRegenerateFail && (
        <div className="mb-m">
          <Notification
            kind="positive"
            size="full"
            overrides={successNotificationOverrides}
            closeable
            onClose={reset}
          >
            {t('regenerate_success')}
          </Notification>
        </div>
      )}

      {data.length === 0 ? (
        <Text.BodyText3 color="grey70">{t('empty')}</Text.BodyText3>
      ) : (
        data.map(response => {
          // Only a stored row has a meaningful timestamp; a fallback body is
          // hardcoded BE copy with no generation date.
          const updated = response.generated
            ? updatedLabel(response.generatedAt)
            : null

          return (
            <div key={response.responseType} className="mb-l">
              <Text.BodyText4 bold color="grey90" className="d-block">
                {t(`types.${response.responseType}`)}:
              </Text.BodyText4>
              <Text.BodyText5 color="grey50" className="d-block mt-xs">
                {response.generated ? t('generated_badge') : t('default_badge')}
              </Text.BodyText5>
              {updated && (
                <Text.BodyText5 color="grey50" className="d-block">
                  {updated}
                </Text.BodyText5>
              )}
              <Text.BodyText3 color="grey70" className="d-block mt-xs">
                {response.body}
              </Text.BodyText3>
            </div>
          )
        })
      )}

      {canManage && (
        <div className="mt-m">
          {/* Stays available on the empty path too — a merchant with no stored
              rows is exactly who needs to force the first build. Disabled in
              flight, and while the post-success GET refetch is still in-flight
              (isFetching, not just isLoading — RTK's isLoading is first-load-only),
              so a merchant can't read "idle button, unchanged timestamp" as
              "nothing happened" and pay for a second generation. */}
          <Button
            kind="secondary"
            onClick={onRegenerate}
            disabled={isRegenerating || isFetching}
            isLoading={isRegenerating}
            aria-label={t('regenerate_aria')}
          >
            {isRegenerating ? t('regenerating') : t('regenerate')}
          </Button>
        </div>
      )}
    </div>
  )
}

export default CannedResponsesPane
